import { cn } from "@/lib/utils";
import * as React from "react";
import { Section } from "./Section";

export interface PhotoBandProps {
  /**
   * Absolute path under `public/`. Provenance, and the rules for adding to
   * `public/photos/`, are in `docs/LEGAL-BLOCKERS.md` §5.
   */
  image: string;
  children: React.ReactNode;
  /**
   * A label or small control pinned to the bottom corner, out of the content's
   * flow — see the note below.
   */
  corner?: React.ReactNode;
  /** `header` for a page title band, `section` for one further down a page. */
  as?: "header" | "section";
  /** Above the fold? Then it is fetched eagerly rather than lazily. */
  eager?: boolean;
  className?: string;
  /** Layout of the content well. Defaults to the page-title band's height. */
  innerClassName?: string;
}

/**
 * One band of the page whose ground is a photograph of the farm.
 *
 * Every photographic band on the site is this component — the page headers on
 * Parcels, My farm and Operations, and the two on the landing page. That is the
 * point: the scrim, the object-fit, the decorative `alt` and the corner slot
 * are decided once, so a new band cannot quietly drift to a different recipe.
 *
 * Two things here are not styling:
 *
 * `alt=""`. The photograph carries nothing the heading beside it does not
 * already say, so a screen reader gains nothing from a description of it.
 *
 * The scrim. White type over a sunlit polytunnel fails contrast without it. It
 * is flat with weighted edges rather than bottom-heavy, because the type sits
 * in the middle of the frame.
 */
const PhotoBand = ({
  image,
  children,
  corner,
  as = "section",
  eager = false,
  className,
  innerClassName,
}: PhotoBandProps) => (
  <Section
    as={as}
    tone="ink"
    size="none"
    bleed
    className={cn("overflow-hidden", className)}
  >
    <img
      src={image}
      alt=""
      aria-hidden
      // A page header is on screen immediately, so it is not lazy — a header
      // that fades in after the content reads as a page still loading. A band
      // further down the page is the opposite case.
      loading={eager ? "eager" : "lazy"}
      fetchPriority={eager ? "high" : "auto"}
      decoding="async"
      className="absolute inset-0 size-full object-cover"
    />
    <div
      aria-hidden
      className="absolute inset-0 bg-kf-ink/40 bg-gradient-to-b from-kf-ink/35 via-transparent to-kf-ink/45"
    />

    <div
      className={cn(
        "container relative flex min-h-[15rem] flex-col items-center justify-center py-14 text-center md:min-h-[19rem] md:py-20",
        innerClassName,
      )}
    >
      {children}
    </div>

    {/*
     * The corner slot exists because of what happened when the parcel count
     * sat under the heading: it can only be known once the parcels have
     * loaded, so it arrived a beat late and pushed the heading up as it did,
     * resizing the band under a photograph already on screen. Out of flow it
     * cannot move anything — the band measures the same with it and without.
     */}
    {corner ? (
      <div className="pointer-events-none absolute inset-x-0 bottom-0 pb-6 md:pb-8">
        <div className="container flex justify-end">
          <span className="pointer-events-auto animate-fade-up">{corner}</span>
        </div>
      </div>
    ) : null}
  </Section>
);

export { PhotoBand };
